import { Fragment, ReactNode, isValidElement, useRef, useState, useEffect, Children, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack, useTheme } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { v4 as uuidv4 } from 'uuid';
import { SnackbarKey, useSnackbar } from 'notistack';
import { AppBaseButton } from '../../components/AppBaseButton/AppBaseButton';
import { addDialogIdToStack, removeDialogIdFromStack, selectActiveDialogId } from '../../redux/slices/dialogSlice';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';

export interface EditDialogProps {
  title: string;
  onClose: () => void;
  handleFormSubmit: () => void;
  error?: FetchBaseQueryError | SerializedError;
  defaultErrorMessage?: string;
  height?: number;
  isDirty: boolean;
  isLoading?: boolean;
  isFormSubmitted?: boolean;
  children: ReactNode;
  saveActionText?: string;
  saveActionTestId?: string;
  saveActionDisabled?: boolean;
  customButtons?: ReactNode;
}

function isDialogContent(children: ReactNode): boolean {
  const checks = Children.map(children, (child) => {
    const dialogContentType = (<DialogContent />).type;
    return isValidElement(child) && child.type === dialogContentType;
  });

  return checks?.length === 1 && checks[0];
}

export function EditDialog({
  title,
  onClose,
  handleFormSubmit,
  error,
  defaultErrorMessage,
  height,
  isDirty,
  isLoading,
  isFormSubmitted,
  children,
  saveActionText,
  saveActionTestId,
  saveActionDisabled,
  customButtons,
}: EditDialogProps): ReactElement {
  const dialogId = useRef(`dialog-${uuidv4()}`);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const activeDialogId = useSelector(selectActiveDialogId);
  const [isDiscardConfirmationOpen, setDiscardConfirmationOpen] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [notificationKey, setNotificationKey] = useState<SnackbarKey | undefined>(undefined);
  const theme = useTheme();
  const projectDialogFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const discardDialogFullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  useEffect(() => {
    const id = dialogId.current;
    dispatch(addDialogIdToStack(id));
    return () => {
      dispatch(removeDialogIdFromStack(id));
    };
  }, [dispatch]);

  const handleOpenDiscardConfirmation = () => {
    setDiscardConfirmationOpen(true);
  };

  const handleCloseDiscardConfirmation = () => {
    setDiscardConfirmationOpen(false);
  };

  const handleNewProjectDialogDiscardConfirm = () => {
    handleCloseDiscardConfirmation();
    onClose();
    if (notificationKey) {
      closeSnackbar();
    }
  };

  const handleNewProjectDialogClose = (reason: string) => {
    if (isLoading) {
      if (reason && reason === 'backdropClick') return;
    }

    if (isDirty) {
      handleOpenDiscardConfirmation();
    } else {
      handleNewProjectDialogDiscardConfirm();
    }
  };

  useEffect(() => {
    if (error) {
      const errorMessage = (error as FetchBaseQueryError)?.data || defaultErrorMessage;
      const key = enqueueSnackbar(errorMessage as string, {
        variant: 'info',
      });
      setNotificationKey(key);
    }
  }, [error, defaultErrorMessage, enqueueSnackbar, t, dispatch]);

  const paperStyle = height
    ? {
        height,
      }
    : {
        minHeight: '90vh',
      };

  return (
    <>
      <Dialog
        PaperProps={{
          sx: paperStyle,
        }}
        data-testid="projectDialog"
        open
        onClose={(event, reason) => {
          if (isLoading && reason === 'backdropClick') return;
          handleNewProjectDialogClose(reason);
        }}
        scroll="paper"
        fullWidth={true}
        maxWidth={'sm'}
        fullScreen={projectDialogFullScreen}
        BackdropProps={{
          appear: false,
          invisible: activeDialogId !== dialogId.current || isDiscardConfirmationOpen,
        }}
        TransitionProps={{ appear: false }}
        sx={
          projectDialogFullScreen
            ? undefined
            : {
                maxHeight: `calc(100% - ${theme.spacing(16)})`,
                marginTop: 8,
                marginBottom: 8,
              }
        }
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box component="span" sx={isLoading ? { paddingTop: '.6rem', paddingBottom: '.625rem' } : null}>
              {title}
            </Box>
            {!isLoading && (
              <IconButton onClick={() => handleNewProjectDialogClose('closeButtonClick')}>
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </DialogTitle>
        <form
          onSubmit={handleFormSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            overflowY: 'auto',
          }}
        >
          {isDialogContent(children) ? (
            <Fragment>{children}</Fragment>
          ) : (
            <DialogContent dividers>
              <Stack spacing={2}>{children}</Stack>
            </DialogContent>
          )}
        </form>
        {!isLoading && !isFormSubmitted && (
          <DialogActions sx={{ padding: '16px 24px', justifyContent: 'flex-end' }}>
            {customButtons != null && customButtons}
            {!customButtons && (
              <AppBaseButton
                variant="contained"
                disabled={saveActionDisabled}
                onClick={() => handleFormSubmit()}
                data-testid={saveActionTestId}
              >
                {saveActionText ? saveActionText : t('common.edit-dialog.action-save')}
              </AppBaseButton>
            )}
          </DialogActions>
        )}
      </Dialog>
      <ConfirmationDialog
        isOpen={isDiscardConfirmationOpen}
        isFullscreen={discardDialogFullScreen}
        handleClose={handleCloseDiscardConfirmation}
        handleConfirm={handleNewProjectDialogDiscardConfirm}
        title={t('common.edit-dialog.discard-dialog.header')}
        questionText={t('common.edit-dialog.discard-dialog.question')}
        cancelText={t('common.edit-dialog.discard-dialog.action-cancel')}
        confirmText={t('common.edit-dialog.discard-dialog.action-confirm')}
      />
    </>
  );
}
